import DetailRow from "../DetailRow";
import { Heading } from "@/shared/components/ui";
import { Accordion, AccordionItem, AccordionContent, AccordionTrigger } from "@/shared/components/ui/accordion";

// utils
import { UIUtils } from "@/shared/utils/UIUtils";
import { CurrencyUtils } from "@/shared/utils/CurrencyUtils";
import { CoverageUtils, PreventiveConfig, PreventiveOption } from "@/shared/utils/CoverageUtils";
import { cn } from "@/shared/utils";

// types
import { Pet, Policy, Quote } from "@/shared/types/Quote.interface";
import { PetBreed } from "@spotpetinsurance/spot-types/entities/PetBreed";
import { PolicySummaryConfig } from "./index";

// media
import Strings from "@/shared/utils/Strings.constants";
import { PetUnderwriterType } from "@spotpetinsurance/spot-types/entities/PetQuote";
import { Button } from "@/shared/components/ui";
import { PreventiveCareModal } from "../PreventiveCareModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCirclePlus } from "@fortawesome/pro-regular-svg-icons";
import { faChevronRight, faDog, faCat } from "@fortawesome/pro-solid-svg-icons";

//statsig test
import { EditCoverageModal } from "@/shared/components/MultiPetUpsell/EditCoverageModal";
import { useAppLayerContext } from "@/shared/contexts/AppLayer";
import { useState } from "react";
import AddPetBanner from "../MultiPetUpsell/AddPetBanner";
import PetModal, { PetModalConfig } from "../PetModal";

interface PolicySummaryContentProps {
    quote: Quote;
    breeds: PetBreed[] | undefined;
    config?: PolicySummaryConfig;
    isAnnualBilling: boolean;
    preventiveOptions?: Record<string, PreventiveOption[]>;
    preventiveConfig?: PreventiveConfig;
    underwriter?: PetUnderwriterType;
    onPreventiveChange?: (value: string, petId?: string, applyToAll?: boolean) => Promise<void>;
    showAddPreventiveCare?: string;
    showAddPetUpsell?: string;
    showEditCoveragePet?: boolean;
    petModalConfig?: PetModalConfig;
    handleActivePetChange?: (value: string) => void;
}

interface PolicySummaryRowProps {
    policy: Policy;
    breedName: string | undefined;
    isAnnualBilling: boolean;
    config?: PolicySummaryConfig;
    quote: Quote;
    isLast: boolean;
    preventiveOptions?: PreventiveOption[];
    onOpenPreventiveModal: (petId: string) => void;
    showAddPreventiveCare?: string;
    policyIndex: number;
    showAddPetUpsell?: string;
    showEditCoveragePet?: boolean;
    preventiveConfig?: PreventiveConfig;
    onEditPlan?: (index: number) => void;
    onEditPet?: (index: number) => void;
}

const PolicySummaryRow = (props: PolicySummaryRowProps) => {
    const { policy, breedName, isAnnualBilling, config, preventiveOptions, onOpenPreventiveModal, policyIndex, preventiveConfig } = props;
    const { partnerOffer } = useAppLayerContext().appState;

    // Set config defaults:
    const { enableAccordionToggle = true, showPreDiscountPrice = true, useGenericDiscountLabel = false, showDiscountId = false, isSingleCol = false } = config ?? {};

    const {
        name,
        species,
        petAge,
        gender,
        basePrice,
        accidentCoverageDate,
        illnessCoverageDate,
        wellnessCoveragePrice,
        wellnessCoverageDate,
        annualLimit,
        annualDeductible,
        reimbursementRate
    } = CoverageUtils.formatPolicyDetails(policy);

    const wellnessCoverage = policy.coverageSettings?.coverages?.find(c => c.type === "wellness-gold" || c.type === "wellness-platinum");
    const hasWellness = !!wellnessCoverage;

    const genderText = gender === "M" ? `Male` : `Female`;
    const hasDifferentAccidentIllnessDates = !!illnessCoverageDate && accidentCoverageDate !== illnessCoverageDate;
    const goldPrice = preventiveOptions?.find(o => o.value === "wellness-gold")?.price ?? 0;

    // Discounts
    const discounts = policy?.discounts ?? [];
    const hasDiscount = discounts.length > 0;
    const totalDiscount = hasDiscount ? discounts.map(discount => discount.amount ?? 0).reduce((previousValue, currentValue) => (previousValue ?? 0) + (currentValue ?? 0), 0) : 0;
    // Needed for generic discount label
    const discountAmount = policy?.discountAmount?.value ?? 0;

    // Pricing
    const planTotal = Number(basePrice?.value ?? 0); //Total before discounts
    const planPremium = planTotal - (wellnessCoveragePrice ?? 0); //Total before discounts minus wellness
    // Total after discounts. Prefer the API's authoritative net price (finalPrice) — same source that plan-price/PriceInfo uses — rather than re-deriving.
    // basePrice is the gross price and discount amounts are positive, so the fallback subtracts (gross - discount = net).
    const planTotalAdjusted = Number(policy.finalPrice?.value ?? planTotal - totalDiscount);

    const textStyles = "text-xs leading-5";

    //statsig test - edit coverage modal
    const handleEditPlan = () => {
        props.onEditPlan?.(policyIndex);
    };

    //statsig test - edit pet modal
    const handleEditPet = () => {
        props.onEditPet?.(policyIndex);
    };

    const showUpsellModal = props.showAddPetUpsell === "inline-cta" || props.showAddPetUpsell === "banner";

    // statsig preventive care test
    const showAddPreventiveCare = props.showAddPreventiveCare === "preventive-coverage" || props.showAddPreventiveCare === "preventive-options";
    const coverageLabel = illnessCoverageDate ? "Accident & Illness" : "Accident Only";

    const formattedWellnessPrice = UIUtils.formatCurrency(wellnessCoveragePrice ?? 0);
    let preventivePriceLabel = formattedWellnessPrice;

    if (!hasWellness && showAddPreventiveCare) {
        preventivePriceLabel = "None Selected";
    }

    const editPetLabelClass = props.showEditCoveragePet ? "font-medium text-content-secondary leading-4" : undefined;

    const renderImportantDates = () => {
        const rows = [];

        if (wellnessCoverageDate) {
            rows.push(
                <DetailRow
                    key="wellness"
                    label="Preventive Care Coverage Starts"
                    labelClass={editPetLabelClass}
                    value={CoverageUtils.calculateCoverageDate(wellnessCoverageDate)}
                    valueClass="font-bold text-content-primary"
                    wrapperClass={textStyles}
                />
            );
        }

        /* Accident & Illness have different dates */
        if (hasDifferentAccidentIllnessDates) {
            if (accidentCoverageDate) {
                rows.push(
                    <DetailRow
                        key="accident"
                        label="Accident Coverage Starts"
                        labelClass={editPetLabelClass}
                        value={CoverageUtils.calculateCoverageDate(accidentCoverageDate)}
                        valueClass="font-bold text-content-primary"
                        wrapperClass={textStyles}
                    />
                );
            }

            if (illnessCoverageDate) {
                rows.push(
                    <DetailRow
                        key="illness"
                        label="Illness Coverage Starts"
                        labelClass={editPetLabelClass}
                        value={CoverageUtils.calculateCoverageDate(illnessCoverageDate)}
                        valueClass="font-bold text-content-primary"
                        wrapperClass={textStyles}
                    />
                );
            }
        } /* Accident & Illness have same date */ else if (accidentCoverageDate && illnessCoverageDate) {
            rows.push(
                <DetailRow
                    key="combined"
                    label="Accident & Illness Coverage Starts"
                    labelClass={editPetLabelClass}
                    value={CoverageUtils.calculateCoverageDate(illnessCoverageDate)}
                    valueClass="font-bold text-content-primary"
                    wrapperClass={textStyles}
                />
            );
        } /* Is Accident Only */ else if (accidentCoverageDate) {
            rows.push(
                <DetailRow
                    key="accidentOnly"
                    label="Accident Coverage Starts"
                    labelClass={editPetLabelClass}
                    value={CoverageUtils.calculateCoverageDate(accidentCoverageDate)}
                    valueClass="font-bold text-content-primary"
                    wrapperClass={textStyles}
                />
            );
        }

        return rows;
    };

    const renderPaymentDetails = () => {
        return (
            <>
                <DetailRow
                    label={showAddPreventiveCare ? coverageLabel : `${isAnnualBilling ? "Annual" : "Monthly"} Premium`}
                    labelClass={editPetLabelClass}
                    value={`${UIUtils.formatCurrency(planPremium)}`}
                    valueClass="font-bold text-content-primary"
                    wrapperClass={textStyles}
                />

                {!!discounts.length &&
                    !useGenericDiscountLabel &&
                    discounts.map((discount, index) => {
                        // Only relabel the partner (priority-code) discount when a partner offer is active.
                        // Other discounts (e.g. the separate multi-pet discount) keep their original label.
                        // The label prefix is partner copy from the Features API; the percentage stays
                        // client-computed from the quote's discount adjustment.
                        //
                        // ENG-18695: gated on `discountLabel`, not on the offer's presence. The multi-pet
                        // experiment puts an offer in this slot for organic traffic, where the only other
                        // discount a quote can carry is an unrelated priority-code one — relabeling that
                        // as the offer would be a lie about where the money came from.
                        const isMultiPetDiscount = !!discount.name?.toLowerCase().includes("multi") || !!discount.id?.toLowerCase().includes("multi");
                        const relabelAsPartnerOffer = !!partnerOffer?.discountLabel && !isMultiPetDiscount;

                        return (
                            <DetailRow
                                key={index}
                                label={
                                    relabelAsPartnerOffer && partnerOffer
                                        ? `${partnerOffer.discountLabel} ${CurrencyUtils.adjustmentToPercent(discount.adjustment)}%`
                                        : showDiscountId
                                          ? discount.id
                                          : discount.name
                                }
                                labelClass="text-content-success"
                                value={`${UIUtils.formatCurrency(discount.amount ?? 0)}`}
                                valueClass={`font-semibold ${showAddPreventiveCare ? "text-content-success" : ""}`}
                                wrapperClass="text-xs leading-5"
                            />
                        );
                    })}

                {useGenericDiscountLabel && discountAmount > 0 && (
                    <DetailRow
                        label="Discount"
                        labelClass={editPetLabelClass}
                        value={`${UIUtils.formatCurrency(-Math.abs(discountAmount))}`}
                        valueClass="font-bold text-content-primary"
                        wrapperClass={`${textStyles} text-content-success`}
                    />
                )}
                {(!!wellnessCoveragePrice || showAddPreventiveCare) && (
                    <DetailRow
                        label={
                            showAddPreventiveCare ? (
                                <span className="flex items-center">
                                    Preventive Care{" "}
                                    {hasWellness && (wellnessCoverage?.type === "wellness-platinum" ? preventiveConfig?.labels.advanced : preventiveConfig?.labels.basic)}
                                    {hasWellness && (
                                        <Button
                                            data-testid="edit-preventive-plan"
                                            variant="ghost"
                                            onClick={() => onOpenPreventiveModal(policy.id!)}
                                            className="h-auto px-2 py-0 text-xs font-medium leading-4 text-content-brand-b underline hover:bg-background-brand-b-secondary hover:text-content-primary focus:outline-none focus:ring-0 focus-visible:outline-none"
                                        >
                                            <span className="underline">Edit plan</span>
                                        </Button>
                                    )}
                                </span>
                            ) : (
                                "Preventive Care Coverage"
                            )
                        }
                        labelClass={editPetLabelClass}
                        value={preventivePriceLabel}
                        valueClass={showAddPreventiveCare && !hasWellness ? "font-bold text-content-secondary leading-4" : "font-bold text-content-primary"}
                        wrapperClass={textStyles}
                    />
                )}
            </>
        );
    };

    return (
        <>
            <AccordionItem value={policy.id!} className="flex flex-col gap-2 text-content-primary">
                <div className="flex items-center justify-between gap-3">
                    {/* Pet Info */}
                    <div className="min-w-0 flex-1 overflow-hidden">
                        <div className="flex items-center gap-3">
                            {props.showEditCoveragePet ? (
                                <button
                                    type="button"
                                    onClick={handleEditPet}
                                    className="group flex min-w-0 cursor-pointer flex-col gap-1 text-left focus:outline-none focus-visible:outline-none"
                                    data-testid={`edit-pet-section-${policy.id}`}
                                    aria-label={`Edit ${name}'s details`}
                                >
                                    <Heading level="h3" className="flex items-center text-base font-bold leading-6">
                                        <FontAwesomeIcon icon={species === "Cat" ? faCat : faDog} className="mr-2 size-5 shrink-0" />
                                        <span className="max-w-[400px] truncate">{name}</span>
                                        <span
                                            data-testid={`edit-pet-name-${policy.id}`}
                                            aria-hidden="true"
                                            className="h-auto shrink-0 px-2 py-0 text-sm font-medium text-content-brand-b underline group-hover:bg-background-brand-b-secondary group-hover:text-content-primary group-focus-visible:bg-background-brand-b-secondary group-focus-visible:text-content-primary"
                                        >
                                            Edit pet
                                        </span>
                                    </Heading>
                                    <p className="text-xs font-medium leading-4">
                                        {petAge}, {genderText}, {breedName}
                                    </p>
                                </button>
                            ) : (
                                <div className="flex min-w-0 flex-col">
                                    <Heading level="h3" className="flex items-center text-base font-bold leading-6">
                                        <span className="max-w-[400px] truncate">{name}</span>
                                        <span className="shrink-0">&apos;s Coverage</span>
                                    </Heading>
                                    <p className={textStyles}>
                                        {species} - {petAge}, {genderText}, {breedName}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {props.showEditCoveragePet && enableAccordionToggle ? (
                        <AccordionTrigger
                            data-testid={`policy-summary-accordion-trigger-${policy.id}`}
                            aria-label={`Toggle ${name}'s policy`}
                            className="flex flex-none shrink-0 items-center justify-end gap-3 rounded py-0 font-normal hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-stroke-brand-b"
                        >
                            <div className="flex flex-col">
                                {hasDiscount && showPreDiscountPrice && (
                                    <span className="text-right text-sm font-medium text-content-secondary line-through">{UIUtils.formatCurrency(planTotal)}</span>
                                )}
                                <span
                                    data-testid="policy-summary-pet-total"
                                    className={`${hasDiscount && showPreDiscountPrice ? "text-content-success" : ""} whitespace-nowrap text-base font-bold`}
                                >
                                    {UIUtils.formatCurrency(planTotalAdjusted)}/{isAnnualBilling ? "yr" : "mo"}
                                </span>
                            </div>
                        </AccordionTrigger>
                    ) : (
                        <div className="flex shrink-0 items-center gap-3">
                            {/* Price */}
                            <div className="flex flex-col">
                                {hasDiscount && showPreDiscountPrice && <span className="text-right text-content-secondary line-through">{UIUtils.formatCurrency(planTotal)}</span>}
                                <span
                                    data-testid="policy-summary-pet-total"
                                    className={`${hasDiscount && showPreDiscountPrice ? "text-content-success" : ""} whitespace-nowrap text-base font-bold`}
                                >
                                    {UIUtils.formatCurrency(planTotalAdjusted)}/{isAnnualBilling ? "yr" : "mo"}
                                </span>
                            </div>
                            {/* Arrow */}
                            {enableAccordionToggle && (
                                <AccordionTrigger
                                    data-testid={`policy-summary-accordion-trigger-${policy.id}`}
                                    aria-label={`Toggle ${name}'s policy`}
                                    className="h-6"
                                ></AccordionTrigger>
                            )}
                        </div>
                    )}
                </div>

                <AccordionContent className="pb-0">
                    <div className={cn("grid grid-cols-1 gap-3", !isSingleCol && "md:grid-cols-2 md:gap-x-6")}>
                        <>
                            {/* Payment Details */}
                            <div className={cn(props.showEditCoveragePet && "order-2")}>
                                {showAddPreventiveCare ? (
                                    <Heading level="h4" className={cn(props.showEditCoveragePet ? "text-sm font-bold leading-5 text-content-primary" : "text-xs font-semibold")}>
                                        Coverage Plans
                                    </Heading>
                                ) : (
                                    <Heading level="h4" className={cn(props.showEditCoveragePet ? "text-sm font-bold leading-5 text-content-primary" : "text-xs font-semibold")}>
                                        Payment Details
                                    </Heading>
                                )}

                                {renderPaymentDetails()}
                            </div>
                            {/* After the AccordionContent, inside AccordionItem */}
                            {showAddPreventiveCare && !hasWellness && (
                                <>
                                    {props.showAddPreventiveCare === "preventive-coverage" && (
                                        <Button
                                            data-testid="preventive-care-coverage"
                                            variant="ghost"
                                            onClick={() => onOpenPreventiveModal(policy.id!)}
                                            startDecorator={<FontAwesomeIcon icon={faCirclePlus} className="size-4" />}
                                            className="-mb-1 -mt-3 w-full rounded-lg border border-stroke-secondary-invert px-3 py-2 text-xs font-bold leading-5 text-content-brand-b hover:bg-background-brand-b-secondary hover:text-content-primary focus:outline-none focus:ring-0 focus-visible:outline-none sm:text-sm md:text-xs lg:text-sm"
                                        >
                                            Add preventive coverage<span className="font-medium">from {UIUtils.formatCurrency(goldPrice)}/mo</span>
                                        </Button>
                                    )}

                                    {props.showAddPreventiveCare === "preventive-options" && (
                                        <Button
                                            data-testid="preventive-care-options"
                                            variant="ghost"
                                            onClick={() => onOpenPreventiveModal(policy.id!)}
                                            className="w-full rounded-lg border border-stroke-secondary-invert px-3 py-2 text-xs font-bold leading-4 text-content-brand-b hover:bg-background-brand-b-secondary hover:text-content-primary focus:outline-none focus:ring-0 focus-visible:outline-none sm:text-sm sm:leading-5 md:text-xs md:leading-4 lg:text-sm lg:leading-5"
                                        >
                                            Check preventive care options <FontAwesomeIcon icon={faChevronRight} className="size-4" />
                                        </Button>
                                    )}
                                </>
                            )}
                        </>
                        {showUpsellModal ? (
                            <>
                                {/* Coverage Details */}
                                <div>
                                    <div className="flex flex-row items-center">
                                        <Heading level="h4" className="text-xs font-semibold">
                                            Coverage Details
                                        </Heading>

                                        <Button
                                            variant="ghost"
                                            className="h-auto px-2 py-0 text-xs font-medium leading-4 text-content-brand-b underline hover:bg-background-brand-b-secondary hover:text-content-primary focus:outline-none focus:ring-0 focus-visible:outline-none"
                                            onClick={handleEditPlan}
                                            data-testid={`edit-plan-${policy.id}`}
                                        >
                                            {Strings.EDIT_PLAN}
                                        </Button>
                                    </div>

                                    <DetailRow label={Strings.ANNUAL_LIMIT} value={annualLimit} valueClass="font-bold text-content-primary" wrapperClass={textStyles} />
                                    <DetailRow label={Strings.REIMBURSEMENT_RATE} value={reimbursementRate} valueClass="font-bold text-content-primary" wrapperClass={textStyles} />
                                    <DetailRow label={Strings.ANNUAL_DEDUCTIBLE} value={annualDeductible} valueClass="font-bold text-content-primary" wrapperClass={textStyles} />
                                </div>
                            </>
                        ) : (
                            <>
                                {/* Coverage Details */}
                                <div className={cn(props.showEditCoveragePet && "order-1")}>
                                    {props.showEditCoveragePet ? (
                                        <div className="mb-3 mt-1 flex flex-row items-center justify-between">
                                            <Heading level="h4" className="text-sm font-bold leading-5 text-content-primary">
                                                Coverage Details
                                            </Heading>
                                            <Button
                                                variant="outline-secondary"
                                                className="mr-1 h-auto border-stroke-brand-b px-2 py-1 text-sm font-medium leading-5 text-content-brand-b hover:border-stroke-transparent hover:bg-background-brand-b-primary hover:text-content-primary-invert"
                                                onClick={handleEditPlan}
                                                data-testid={`edit-pet-plan-${policy.id}`}
                                            >
                                                Edit plan
                                            </Button>
                                        </div>
                                    ) : (
                                        <Heading level="h4" className="text-xs font-semibold">
                                            Coverage Details
                                        </Heading>
                                    )}

                                    <DetailRow
                                        label={Strings.ANNUAL_LIMIT}
                                        labelClass="font-medium text-content-secondary leading-4"
                                        value={annualLimit}
                                        valueClass="font-bold text-content-primary"
                                        wrapperClass={textStyles}
                                    />
                                    <DetailRow
                                        label={Strings.REIMBURSEMENT_RATE}
                                        labelClass="font-medium text-content-secondary leading-4"
                                        value={reimbursementRate}
                                        valueClass="font-bold text-content-primary leading-4"
                                        wrapperClass={textStyles}
                                    />
                                    <DetailRow
                                        label={Strings.ANNUAL_DEDUCTIBLE}
                                        labelClass="font-medium text-content-secondary"
                                        value={annualDeductible}
                                        valueClass="font-bold text-content-primary"
                                        wrapperClass={textStyles}
                                    />
                                </div>
                            </>
                        )}
                        {/* Important Dates */}
                        <div className={cn(props.showEditCoveragePet && "order-3")}>
                            <Heading
                                level="h4"
                                className={cn(props.showEditCoveragePet ? "text-sm font-bold leading-5 text-content-primary" : "text-xs font-bold text-content-primary")}
                            >
                                Important Dates
                            </Heading>

                            {renderImportantDates()}
                        </div>
                    </div>
                </AccordionContent>
            </AccordionItem>

            {/* // statsig test - add a pet banner */}
            {props.showAddPetUpsell === "inline-cta" && props.isLast && <AddPetBanner variant="inline-cta" />}

            {/* /// Divider /// */}
            <div className="border-b border-stroke-tertiary"></div>
        </>
    );
};

const PolicySummaryContent = (props: PolicySummaryContentProps) => {
    const [modalPetId, setModalPetId] = useState<string | null>(null);
    const {
        quote,
        breeds,
        config,
        isAnnualBilling,
        preventiveOptions,
        preventiveConfig,
        underwriter,
        onPreventiveChange,
        showAddPetUpsell,
        showEditCoveragePet,
        petModalConfig,
        handleActivePetChange
    } = props;
    const policies = quote?.policies ?? [];
    const policyIds: Policy["id"][] = [];

    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editPetModalOpen, setEditPetModalOpen] = useState(false);
    const [editPetIndex, setEditPetIndex] = useState<number | null>(null);
    const { updateAppState, appState } = useAppLayerContext();
    const isMultiPetLinked = !!appState.isMultiPetLinked;

    const handleEditPlan = (index: number) => {
        updateAppState({ activePetIndex: index });
        setEditModalOpen(true);
    };

    const handleEditPet = (index: number) => {
        updateAppState({ activePetIndex: index });
        setEditPetIndex(index);
        setEditPetModalOpen(true);
    };

    const summaryRows = policies.map((policy, index) => {
        // Policy ids needed to open all policies in the accordion by default
        policyIds.push(policy.id);
        const breedName = breeds?.find(breed => breed.id === policy.breedID)?.name ?? "";
        const isLast = index + 1 === policies.length;
        return (
            <PolicySummaryRow
                policy={policy}
                breedName={breedName}
                isAnnualBilling={isAnnualBilling}
                key={index}
                config={config}
                quote={quote}
                isLast={isLast}
                preventiveOptions={props.preventiveOptions?.[policy.id ?? ""]}
                onOpenPreventiveModal={petId => setModalPetId(petId)}
                showAddPreventiveCare={props.showAddPreventiveCare}
                policyIndex={index}
                showAddPetUpsell={showAddPetUpsell}
                showEditCoveragePet={showEditCoveragePet}
                onEditPlan={handleEditPlan}
                onEditPet={handleEditPet}
                preventiveConfig={preventiveConfig}
            />
        );
    });

    const editPetPolicy = editPetIndex !== null ? policies[editPetIndex] : undefined;

    const activePet = policies.find(p => p.id === modalPetId);
    const currentWellness = activePet?.coverageSettings?.coverages?.find(c => c.type === "wellness-gold" || c.type === "wellness-platinum");

    return (
        <>
            <Accordion type="multiple" defaultValue={policyIds.filter(id => id !== undefined) as string[]} className="flex flex-col gap-4 p-4">
                {summaryRows}
                <EditCoverageModal isOpen={editModalOpen} onClose={() => setEditModalOpen(false)} />
                {showEditCoveragePet && petModalConfig && editPetPolicy && (
                    <PetModal
                        key={editPetIndex ?? undefined}
                        initialValues={{
                            policies: isMultiPetLinked && policies.length > 1 ? [editPetPolicy, ...policies.filter(p => p.id !== editPetPolicy.id)] : [editPetPolicy]
                        }}
                        isOpen={editPetModalOpen}
                        onClose={() => setEditPetModalOpen(false)}
                        config={petModalConfig}
                        handleActivePetChange={handleActivePetChange ?? (() => {})}
                    />
                )}
            </Accordion>
            {modalPetId && activePet && preventiveConfig && underwriter && (
                <PreventiveCareModal
                    isOpen={!!modalPetId}
                    onClose={() => setModalPetId(null)}
                    showMultiPetToggle={policies.length > 1}
                    activePet={activePet as Pet}
                    preventiveOptions={preventiveOptions?.[modalPetId] ?? []}
                    preventiveConfig={preventiveConfig}
                    underwriter={underwriter}
                    wellnessValue={(currentWellness?.type as any) ?? "None"}
                    onSave={async (value, applyToAll) => {
                        if (!onPreventiveChange) return;
                        await onPreventiveChange(value, modalPetId ?? undefined, applyToAll);
                    }}
                />
            )}
        </>
    );
};

export default PolicySummaryContent;
